import React, { createContext, useReducer, useContext, useMemo } from 'react';

import { Section } from './dataHooks';

export const REVIEW_STATUS_VALUES = {
  // not applicable, only keeping here to keep in sync with backend
  NOT_ASSIGNED: 'NOT_ASSIGNED',
  ASSIGNED: 'ASSIGNED',
  IN_PROGRESS: 'IN_PROGRESS',
  MANAGER_SUBMITTED: 'MANAGER_SUBMITTED',
  PUBLISHED_TO_MANAGER: 'PUBLISHED_TO_MANAGER',
  PUBLISHED_TO_EMPLOYEE: 'PUBLISHED_TO_EMPLOYEE',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
};

export const REVIEW_STATUS_LABELS = {
  [REVIEW_STATUS_VALUES.NOT_ASSIGNED]: 'Not assigned',
  [REVIEW_STATUS_VALUES.ASSIGNED]: 'Assigned',
  [REVIEW_STATUS_VALUES.IN_PROGRESS]: 'In progress',
  [REVIEW_STATUS_VALUES.MANAGER_SUBMITTED]: 'Manager submitted',
  [REVIEW_STATUS_VALUES.PUBLISHED_TO_MANAGER]: 'Published to manager',
  [REVIEW_STATUS_VALUES.PUBLISHED_TO_EMPLOYEE]: 'Published to employee',
  [REVIEW_STATUS_VALUES.ACKNOWLEDGED]: 'Acknowledged',
};

export const REVIEW_STATUS_COLORS = {
  [REVIEW_STATUS_VALUES.NOT_ASSIGNED]: 'red',
  [REVIEW_STATUS_VALUES.ASSIGNED]: 'grey',
  [REVIEW_STATUS_VALUES.IN_PROGRESS]: 'yellow',
  [REVIEW_STATUS_VALUES.MANAGER_SUBMITTED]: 'green',
  [REVIEW_STATUS_VALUES.PUBLISHED_TO_MANAGER]: 'green',
  [REVIEW_STATUS_VALUES.PUBLISHED_TO_EMPLOYEE]: 'darkGreen',
  [REVIEW_STATUS_VALUES.ACKNOWLEDGED]: 'darkGreen',
};

export type Screen = 'PUBLISH_OPTIONS' | 'EMPLOYEES_LIST' | 'RESULT';

type ActionType = 'SET_SCREEN' | 'TOGGLE_DRAWER' | 'SET_SELECTED_ACTION' | 'RESET_STATE' | 'SET_PUBLISH_SUCCESSFUL';

interface State {
  screen: Screen;
  isOpen: boolean;
  selectedSection: Section;
  publishSuccessful: boolean;
}

export const PARTIAL_PUBLISH_SCREENS: Record<Screen, Screen> = {
  PUBLISH_OPTIONS: 'PUBLISH_OPTIONS',
  EMPLOYEES_LIST: 'EMPLOYEES_LIST',
  RESULT: 'RESULT',
};

const STATE: State = {
  screen: PARTIAL_PUBLISH_SCREENS.PUBLISH_OPTIONS,
  isOpen: false,
  selectedSection: null,
  publishSuccessful: null,
};

type Reducer = (state: State, action: { type: ActionType; payload?: any }) => typeof STATE;

interface PartialPublishContextType extends State {
  setScreen: (screen: Screen) => void;
  toggleDrawer: (isOpen: boolean) => void;
  setSelectedSection: (section: Section) => void;
  resetState: () => void;
  setPublishSuccessful: (success: boolean) => void;
}

const PartialPublishContext = createContext<PartialPublishContextType>(null);

export const PartialPublishContextProvider = ({ children }) => {
  // eslint-disable-next-line no-shadow
  const [state, dispatch] = useReducer<Reducer>((state, action) => {
    switch (action.type) {
      case 'SET_SCREEN': {
        return {
          ...state,
          screen: action.payload,
        };
      }
      case 'TOGGLE_DRAWER': {
        return {
          ...state,
          isOpen: action.payload,
        };
      }
      case 'SET_SELECTED_ACTION': {
        return {
          ...state,
          selectedSection: action.payload,
        };
      }
      case 'RESET_STATE': {
        return STATE;
      }
      case 'SET_PUBLISH_SUCCESSFUL': {
        return {
          ...state,
          publishSuccessful: action.payload,
        };
      }
      default: {
        return { ...state };
      }
    }
  }, STATE);

  const setScreen = (screen: Screen) => {
    dispatch({ type: 'SET_SCREEN', payload: screen });
  };

  const toggleDrawer = (isOpen: boolean) => {
    dispatch({ type: 'TOGGLE_DRAWER', payload: isOpen });
  };

  const setSelectedSection = (section: Section) => {
    dispatch({ type: 'SET_SELECTED_ACTION', payload: section });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  const setPublishSuccessful = (success: boolean) => {
    dispatch({ type: 'SET_PUBLISH_SUCCESSFUL', payload: success });
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      setScreen,
      toggleDrawer,
      setSelectedSection,
      resetState,
      setPublishSuccessful,
    }),
    [state]
  );

  return <PartialPublishContext.Provider value={memoizedState}>{children}</PartialPublishContext.Provider>;
};

export const usePartialPublishContext = () => useContext(PartialPublishContext);
